import { authClient } from "./auth-client";
import type { AuthClientError } from "./auth-errors";

/**
 * Password reset against platform-api's Better Auth endpoints. The gateway
 * mails a link to the web entry's `/reset-password` page and picks that page
 * itself, so no `redirectTo` is sent from here: a desktop client's own origin
 * is a loopback address the browser opening the letter has nothing at.
 *
 * `not-configured` and `unavailable` stay apart because a lawyer is told
 * different things: password reset is not part of this deployment, versus the
 * service is down for now.
 */
export type PasswordResetFailure =
  | { readonly status: "not-configured" }
  | { readonly status: "unavailable" }
  | { readonly status: "rate-limited" }
  | { readonly status: "rejected"; readonly error: AuthClientError };

export type PasswordResetRequestOutcome = { readonly status: "sent" } | PasswordResetFailure;

export type PasswordResetOutcome =
  | { readonly status: "done" }
  | { readonly status: "invalid-token" }
  | PasswordResetFailure;

/** The gateway's answer when mail or the web entry is absent from the deployment. */
const NOT_CONFIGURED_CODE = "PASSWORD_RESET_NOT_CONFIGURED";

function classify(error: AuthClientError): PasswordResetFailure {
  if (error.code === NOT_CONFIGURED_CODE) return { status: "not-configured" };
  if (error.status === 429) return { status: "rate-limited" };
  if (error.status === undefined || error.status === 0 || error.status >= 500) {
    return { status: "unavailable" };
  }
  return { status: "rejected", error };
}

/**
 * Asks for a reset letter. A known and an unknown address get the same answer,
 * so `sent` means only that the request was accepted.
 */
export async function requestPasswordReset(email: string): Promise<PasswordResetRequestOutcome> {
  try {
    const { error } = await authClient.requestPasswordReset({ email });
    return error ? classify(error) : { status: "sent" };
  } catch {
    // better-fetch rethrows a network failure instead of returning it.
    return { status: "unavailable" };
  }
}

/** Sets a new password with the token the mailed link carried. */
export async function resetPassword(input: {
  readonly token: string;
  readonly newPassword: string;
}): Promise<PasswordResetOutcome> {
  try {
    const { error } = await authClient.resetPassword(input);
    if (!error) return { status: "done" };
    if (error.code === "INVALID_TOKEN") return { status: "invalid-token" };
    return classify(error);
  } catch {
    return { status: "unavailable" };
  }
}
